import { useRef, useState } from 'react';
import { findId, findIdErrorMessage } from '@shared/api/candidateApi.ts';
import { Alert, Button } from '@shared/components/Button.tsx';
import { CandidateIdBox } from '@shared/components/CandidateIdBox.tsx';
import { Field, TextInput } from '@shared/components/Field.tsx';
import { Turnstile, type TurnstileHandle } from '@shared/components/Turnstile.tsx';
import { normalizeMobile } from '@shared/lib/candidateForm.ts';
import { PageBanner } from '../Layout.tsx';

const isMobile = (v: string) => /^[6-9]\d{9}$/.test(normalizeMobile(v));

export function FindIdPage() {
  const [phone, setPhone] = useState('');
  const [error, setError] = useState<string>();
  const [submitError, setSubmitError] = useState<string>();
  const [submitting, setSubmitting] = useState(false);
  const [candidates, setCandidates] = useState<{ id: string; name: string }[]>();
  /** Turnstile token for the NEXT submission — never stored, never reused. */
  const [botToken, setBotToken] = useState<string | null>(null);
  const botRef = useRef<TurnstileHandle>(null);

  const submit = async () => {
    if (!isMobile(phone)) {
      setError('कृपया वैध १० अंकी मोबाईल क्रमांक भरा.');
      return;
    }
    if (!botToken) return;
    setError(undefined);
    setSubmitError(undefined);
    setSubmitting(true);
    try {
      const res = await findId(normalizeMobile(phone), botToken);
      setCandidates(res.candidates);
    } catch (err) {
      setSubmitError(findIdErrorMessage(err));
      // Tokens are single-use: always get a fresh one before the next attempt.
      botRef.current?.reset();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageBanner title='नोंदणी आयडी शोधा' />
      <div className='container content content--narrow'>
        <p className='step-sub'>
          नोंदणी करताना वापरलेला मोबाईल क्रमांक भरा — या फोन क्रमांकाशी जोडलेले सर्व ID प्रदर्शित होतील. त्यामधून आपल्या ID ची नोंद करावी.
        </p>

        {candidates ? (
          <>
            {candidates.length > 1 && (
              <p className='step-sub'>
                या मोबाईल क्रमांकावर एकापेक्षा जास्त नोंदणी सापडल्या:
              </p>
            )}
            {candidates.map((c) => (
              <CandidateIdBox key={c.id} id={c.id} name={candidates.length > 1 ? c.name : undefined} />
            ))}
          </>
        ) : (
          <>
            <Field id='find_id_phone' label='मोबाईल क्रमांक' required error={error}>
              <TextInput id='find_id_phone' type='tel' inputMode='numeric' value={phone} maxLength={14}
                placeholder='98XXXXXXXX' invalid={!!error}
                onChange={(ev) => setPhone(ev.target.value)} />
            </Field>
            <div style={{ marginTop: 16 }}>
              <Turnstile ref={botRef} onToken={setBotToken} />
            </div>
            {submitError && (
              <div style={{ marginTop: 16 }}>
                <Alert tone='error' title='आयडी सापडला नाही'>{submitError}</Alert>
              </div>
            )}
            <div style={{ marginTop: 20 }}>
              <Button size='lg' onClick={submit} disabled={!botToken} loading={submitting} loadingText='शोधत आहे…'>
                आयडी शोधा
              </Button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
